import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Activity, LogOut, MessageSquareText, Settings2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { api, setCsrfToken } from '../../lib/api'
import { cn } from '../../lib/cn'
import type { AuthSession } from '../../types'
import { Button } from '../ui/button'
import { useToast } from '../ui/toast'

const navigation = [
  { to: '/messages', label: 'Messaggi', icon: MessageSquareText },
  { to: '/settings', label: 'Configurazione', icon: Settings2 },
  { to: '/system', label: 'Sistema', icon: Activity },
]

export function AppShell({
  children,
  username,
  role,
}: {
  children: ReactNode
  username: string
  role: NonNullable<AuthSession['role']>
}) {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const visibleNavigation =
    role === 'admin' ? navigation : navigation.filter((item) => item.to === '/messages')
  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      setCsrfToken()
      queryClient.clear()
      window.location.assign('/')
    },
    onError: () => showToast('Disconnessione non riuscita', 'error'),
  })

  return (
    <div className="min-h-svh bg-[var(--background)] lg:grid lg:grid-cols-[88px_minmax(0,1fr)]">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[88px] flex-col items-center border-r border-[var(--border)] bg-[var(--surface)] py-5 lg:flex">
        <div className="grid size-12 place-items-center rounded-2xl bg-[var(--primary)] text-sm font-black text-[var(--primary-foreground)] shadow-sm">
          P2
        </div>
        <nav
          aria-label="Navigazione principale"
          className="mt-10 flex w-full flex-col items-center gap-2 px-3"
        >
          {visibleNavigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              aria-label={item.label}
              title={item.label}
              className={({ isActive }) =>
                cn(
                  'grid size-12 place-items-center rounded-2xl text-[var(--muted-foreground)] transition',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
                  isActive
                    ? 'bg-[var(--primary-soft)] text-[var(--primary)]'
                    : 'hover:bg-[var(--muted)] hover:text-[var(--foreground)]',
                )
              }
            >
              <item.icon className="size-5" />
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto flex flex-col items-center gap-3">
          <div
            className="grid size-10 place-items-center rounded-full bg-[var(--muted)] text-sm font-bold uppercase text-[var(--foreground)]"
            title={`${username} · ${role === 'admin' ? 'Amministratore' : 'Operatore chat'}`}
          >
            {username.slice(0, 2)}
          </div>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Esci"
            title="Esci"
            onClick={() => logout.mutate()}
          >
            <LogOut className="size-5" />
          </Button>
        </div>
      </aside>

      <div className="lg:col-start-2">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-[var(--border)] bg-[var(--surface)]/95 px-4 backdrop-blur lg:hidden">
          <div className="flex items-center gap-3">
            <div className="grid size-9 place-items-center rounded-xl bg-[var(--primary)] text-xs font-black text-[var(--primary-foreground)]">
              P2
            </div>
            <span className="font-bold tracking-tight">PEMMY2</span>
          </div>
          <Button variant="ghost" size="icon" aria-label="Esci" onClick={() => logout.mutate()}>
            <LogOut className="size-5" />
          </Button>
        </header>
        <main className="pb-[calc(72px+env(safe-area-inset-bottom))] lg:min-h-svh lg:pb-0">
          {children}
        </main>
        <nav
          aria-label="Navigazione mobile"
          className={cn(
            'fixed inset-x-0 bottom-0 z-40 grid h-[calc(64px+env(safe-area-inset-bottom))] border-t border-[var(--border)] bg-[var(--surface)]/98 px-2 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden',
            role === 'admin' ? 'grid-cols-3' : 'grid-cols-1',
          )}
        >
          {visibleNavigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex min-h-11 flex-col items-center justify-center gap-1 rounded-xl text-[11px] font-semibold text-[var(--muted-foreground)]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ring)]',
                  isActive && 'text-[var(--primary)]',
                )
              }
            >
              <item.icon className="size-5" />
              {item.label === 'Configurazione' ? 'Configura' : item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  )
}
